import { CASE_TYPE } from "@demandproai/shared";
import { FORM_STEPS } from "../constants/formSteps";
import { TOUR_DEFAULT_PLACEMENT, TOUR_TARGET_WAIT_MS } from "../constants/tour";
import { TOUR_TARGETS, tourSelector } from "../constants/tourTargets";
import { waitForElement } from "../utils/helper";
import {
   hasCaseBasics,
   hasLiabilityNarrative,
   hasNamedDefendant,
   hasNamedPlaintiff,
   hasPainDetail,
   hasPostAccidentProvider,
} from "./editCaseTourRequirements";

// Case types that render the liability narrative fields
const LIABILITY_NARRATIVE_CASE_TYPES = [
   CASE_TYPE.AUTO_ACCIDENT,
   CASE_TYPE.DOG_BITE,
   CASE_TYPE.TRIP_AND_FALL,
   CASE_TYPE.SLIP_AND_FALL,
   CASE_TYPE.PREMISE_LIABILITY,
];

// Ordered tour content. formStep is the wizard step that has to be showing for
// the target to exist. isAvailable drops a stop this case will never render, so
// the tour does not stall waiting on a target that is not coming. requirement
// holds Next until the stop has been carried out; stops without one are read
// only and always advance.
const EDIT_CASE_TOUR_STOPS = [
   {
      target: TOUR_TARGETS.PROGRESS_BAR,
      formStep: FORM_STEPS.CASE_INFO,
      title: "Five steps to a demand",
      content:
         "Work through each step in order, or click any label to jump around. Everything saves as you type, so you can leave and come back.",
      placement: "bottom",
   },
   {
      target: TOUR_TARGETS.CASE_BASICS,
      formStep: FORM_STEPS.CASE_INFO,
      title: "Start with the basics",
      content:
         "Name, type, date and state. Case type decides which analysis runs later, so it is worth getting right up front.",
      requirement: {
         isSatisfied: hasCaseBasics,
         hint: "Fill in a case name and the date of incident to carry on.",
      },
   },
   {
      target: TOUR_TARGETS.CASE_PLAINTIFFS,
      formStep: FORM_STEPS.CASE_INFO,
      title: "Add your plaintiffs",
      content:
         "Everyone claiming on this case. Each one gets their own tab on the medical, pain and damages steps.",
      requirement: {
         isSatisfied: hasNamedPlaintiff,
         hint: "Add at least one plaintiff with a name to carry on.",
      },
   },
   {
      target: TOUR_TARGETS.CASE_DEFENDANTS,
      formStep: FORM_STEPS.CASE_INFO,
      title: "Add your defendants",
      content:
         "Everyone at fault. Each one gets their own tab on the liability step so you can treat them separately.",
      requirement: {
         isSatisfied: hasNamedDefendant,
         hint: "Add at least one defendant with a name to carry on.",
      },
   },
   {
      target: TOUR_TARGETS.IMPORT_PARTNER,
      formStep: FORM_STEPS.CASE_INFO,
      title: "Already have this case elsewhere?",
      content:
         "If the case lives in a partner system, import it here instead of typing everything twice.",
   },
   {
      target: TOUR_TARGETS.LIABILITY_NARRATIVE,
      formStep: FORM_STEPS.LIABILITY,
      title: "Tell us how it happened",
      content:
         "Describe the incident in your own words. This lands close to verbatim in the demand, so detail here pays off.",
      isAvailable: ({ caseType }) => LIABILITY_NARRATIVE_CASE_TYPES.includes(caseType),
      requirement: {
         isSatisfied: hasLiabilityNarrative,
         hint: "Describe how the incident happened to carry on.",
      },
   },
   {
      target: TOUR_TARGETS.LIABILITY_REPORTS,
      formStep: FORM_STEPS.LIABILITY,
      title: "Back it up with reports",
      content:
         "Police report, witness statements, incident report, expert safety report. These become exhibits on the letter.",
   },
   {
      target: TOUR_TARGETS.MEDICAL_PROVIDERS_POST,
      formStep: FORM_STEPS.MEDICAL_PROVIDERS,
      title: "Every provider seen after the incident",
      content:
         "Add each one with their records. The order you put them in is the treatment narrative the demand follows.",
      requirement: {
         isSatisfied: hasPostAccidentProvider,
         hint: "Add at least one provider to carry on.",
      },
   },
   {
      target: TOUR_TARGETS.MEDICAL_PROVIDERS_PRIOR,
      formStep: FORM_STEPS.MEDICAL_PROVIDERS,
      title: "Do not skip prior records",
      content:
         "The other side will argue the injury pre-dated the incident. Getting ahead of that is worth more than leaving it out.",
   },
   {
      target: TOUR_TARGETS.MEDICAL_SUMMARY,
      formStep: FORM_STEPS.MEDICAL_PROVIDERS,
      title: "Generate the medical summary",
      content:
         "Once records are uploaded, generate the summary here. It takes a few minutes and uses a credit.",
   },
   {
      target: TOUR_TARGETS.PAIN_POINTS,
      formStep: FORM_STEPS.PAIN_AND_SUFFERING,
      title: "How life changed",
      content:
         "Tick what applies and describe the day to day impact. This section does real work in the final letter.",
      requirement: {
         isSatisfied: hasPainDetail,
         hint: "Tick a pain point or describe the impact to carry on.",
      },
   },
   {
      target: TOUR_TARGETS.INJURY_PHOTOS,
      formStep: FORM_STEPS.PAIN_AND_SUFFERING,
      title: "Add injury photos",
      content:
         "Photos of visible injuries are the strongest single exhibit you can put in front of an adjuster.",
   },
   {
      target: TOUR_TARGETS.ECONOMIC_DAMAGES,
      formStep: FORM_STEPS.ECONOMIC_DAMAGE,
      title: "Lost earnings and future costs",
      content:
         "Upload backup for anything you claim here. Unsupported numbers are the first thing an adjuster challenges.",
   },
   {
      target: TOUR_TARGETS.GENERATE_DEMAND,
      formStep: FORM_STEPS.ECONOMIC_DAMAGE,
      title: "That is the whole flow",
      content:
         "When the case is complete, hit Generate Demand and we will draft the letter. You can keep editing until then.",
   },
];

// The stops this case actually renders, in the order the tour visits them, so a
// running tour's index can be mapped back to the stop it is showing
export const getEditCaseTourStops = ({ caseType }) =>
   EDIT_CASE_TOUR_STOPS.filter(({ isAvailable }) => !isAvailable || isAvailable({ caseType }));

export const buildEditCaseTourSteps = ({ caseType, goToFormStep }) =>
   getEditCaseTourStops({ caseType }).map(
      ({ target, formStep, title, content, placement }) => {
         const selector = tourSelector(target);

         return {
            target: selector,
            title,
            content,
            placement: placement ?? TOUR_DEFAULT_PLACEMENT,
            // Joyride calls .then on this unconditionally, so it always resolves.
            // A step with a before hook never reaches the library's own target
            // polling, and an anchor that is not mounted the moment this settles
            // is skipped outright, so the wait belongs here
            before: async () => {
               await goToFormStep(formStep);
               await waitForElement({ selector, timeoutMs: TOUR_TARGET_WAIT_MS });
            },
         };
      }
   );

export default EDIT_CASE_TOUR_STOPS;
